/**
 * Put a registered person on the watchlist — the face report's counterpart to the vehicle one.
 *
 * ─── WHY THIS IS A FORM AND NOT A PROMPT ───
 * Flagging somebody used to be `prompt("Reason?")`. That collected the one field that changes
 * nothing and silently defaulted the two that change everything: every flagged person came out
 * CRITICAL with alerting ON, because that is what the node assumes when it is told nothing. So
 * the only way to flag an ex-contractor quietly — visible in the reports, not on the video wall
 * at 3am — was an API call. The decision is now made where it is taken.
 *
 * ─── THE FIELDS THEMSELVES LIVE NEXT DOOR ───
 * In `PersonWatchlistForm`, because the registration modals offer the same decision at the
 * moment somebody is first enrolled and two copies of it would drift. This file is what wraps
 * them for an EXISTING person: the consequence banner naming them, the remove button, and the
 * save. See that file for why the fields are ordered as they are.
 *
 * ─── IT EDITS, NOT JUST ADDS ───
 * Opening it on somebody already flagged fills in what they were flagged with, so changing a
 * severity or silencing an existing flag is one dialog rather than un-flagging and starting
 * again — which would lose the reason and the date they were listed.
 *
 * ─── THE DIALOG WEARS THE CATEGORY'S COLOUR ───
 * Banner, title icon and confirm button all take the hue of the category currently chosen, and
 * change the instant it is changed. The dialog is a decision about A PERSON, and the thing most
 * worth getting wrong-proof is which kind of decision it is: red for barred, violet for wanted,
 * sky for missing. A form that stayed red throughout would be telling the operator that listing
 * a missing child is the same act as barring an intruder.
 */
import { useEffect, useState } from 'react';
import { NoSymbolIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import PersonWatchlistForm, {
  DEFAULT_PERSON_WATCHLIST, personWatchlistCategory, type PersonWatchlistValue,
} from './PersonWatchlistForm';
import { WATCHLIST_SOLID_BTN } from '../common/watchlistForm';
import { BTN_SECONDARY } from '../common/ui';

export interface PersonBlacklistTarget {
  person_id: string;
  name?: string | null;
  is_blacklisted?: boolean;
  blacklist_reason?: string | null;
  blacklist_category?: string | null;
  blacklist_severity?: string | null;
  blacklist_alert?: boolean;
}

/** The entry this dialog saves. Named for the endpoint; shared with the registration modals. */
export type PersonBlacklistValue = PersonWatchlistValue;

export interface PersonBlacklistDialogProps {
  /** The person being flagged, or null when the dialog is closed. */
  person: PersonBlacklistTarget | null;
  onClose: () => void;
  /** Save. Rejecting shows the message in the dialog and keeps it open. */
  onSubmit: (value: PersonBlacklistValue) => Promise<void>;
  /** Take an already-flagged person off the list. Omitted for a person who is not on it. */
  onRemove?: () => Promise<void>;
}

export default function PersonBlacklistDialog({
  person, onClose, onSubmit, onRemove,
}: PersonBlacklistDialogProps) {
  const listed = !!person?.is_blacklisted;

  const [entry, setEntry] = useState<PersonWatchlistValue>(DEFAULT_PERSON_WATCHLIST);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seeded whenever a different person is opened, so the form never shows the last one's
  // reason against this one's name.
  useEffect(() => {
    if (!person) return;
    setEntry({
      reason: person.blacklist_reason || null,
      category: person.blacklist_category || DEFAULT_PERSON_WATCHLIST.category,
      severity: person.blacklist_severity || 'critical',
      alert: person.blacklist_alert !== false,
    });
    setError(null);
  }, [person]);

  if (!person) return null;
  const name = person.name || person.person_id;
  const cat = personWatchlistCategory(entry.category);
  const alertName = cat?.alertName || 'watchlist';

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onClose();
    } catch (err: any) {
      // The node's own message, which names what it objected to.
      setError(err?.response?.data?.message || err?.message || 'Could not save that');
    } finally {
      setBusy(false);
      setRemoving(false);
    }
  }

  function save() {
    run(() => onSubmit({ ...entry, reason: entry.reason?.trim() || null }));
  }

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      // `md`, not `sm`: two three-up segmented rows and a paragraph of consequence do not fit
      // a 28rem column without the category labels wrapping mid-word.
      size="md"
      ariaLabel={listed ? 'Edit watchlist entry' : 'Add to watchlist'}
      title={
        <span className="flex items-center gap-2">
          <NoSymbolIcon className={`h-5 w-5 shrink-0 ${cat?.accent || 'text-red-500'}`} />
          {listed ? 'Edit watchlist entry' : 'Add to watchlist'}
        </span>
      }
      subtitle={name}
      footer={
        <div className="flex items-center gap-2">
          {listed && onRemove && (
            <button
              type="button"
              disabled={busy}
              onClick={() => { setRemoving(true); run(onRemove); }}
              className="inline-flex h-9 items-center justify-center rounded-lg border border-destructive/30 px-3.5 text-[13px] font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {removing ? 'Removing…' : 'Remove from watchlist'}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className={`ml-auto ${BTN_SECONDARY}`}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={save}
            className={`${WATCHLIST_SOLID_BTN} ${cat?.solid || 'bg-red-500 hover:bg-red-600'}`}
          >
            {/* A save that reaches the node and back is not instant, and a dead-looking button
                gets clicked twice. */}
            {busy && !removing && (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            )}
            {busy && !removing ? 'Saving…' : listed ? 'Save changes' : 'Add to watchlist'}
          </button>
        </div>
      }
    >
      {/* The Modal shell is padding-free by design — content owns its inset. */}
      <div className="space-y-4 px-5 py-4">
        {/* What listing them actually does, before the fields that tune it. The vehicle panel
            says the same thing in its header; here it is the first thing in the dialog because
            this one is opened from a single row rather than from a list about watching.
            Tinted with the category, so the sentence and its colour say the same thing. */}
        <p className={`rounded-lg px-3 py-2.5 text-[12px] leading-relaxed ${
          entry.alert ? cat?.banner || 'bg-muted text-muted-foreground' : 'bg-muted text-muted-foreground'
        }`}>
          Every camera that recognises <span className="font-semibold">{name}</span> will flag
          the sighting
          {entry.alert
            ? <> and raise a <span className="font-semibold">{alertName}</span> alert</>
            : ', but no alert will be raised'}. Sightings already recorded keep whatever verdict
          they were given at the time.
        </p>

        <PersonWatchlistForm
          value={entry}
          onChange={setEntry}
          disabled={busy}
          autoFocusReason
          onEnter={save}
        />

        {error && (
          <p className="flex items-start gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-[12px] leading-snug text-red-500">
            <ExclamationCircleIcon className="mt-px h-4 w-4 shrink-0" />
            <span className="min-w-0">{error}</span>
          </p>
        )}
      </div>
    </Modal>
  );
}
